/**
 * The query keys for the rebuilt pages, by service, in one place. Every
 * key starts with its service, so an action can refetch all of one
 * service's reads at once (`["envoy"]`).
 */
export const keys = {
    envoy: {
        all: ["envoy"] as const,
        overview: (timeZone: string) => ["envoy", "overview", timeZone] as const,
        overviewDays: (timeZone: string, days: number) =>
            ["envoy", "overview", timeZone, days] as const,
        queue: ["envoy", "queue"] as const,
        ready: ["envoy", "ready"] as const,
        needsYou: ["envoy", "needs-you"] as const,
        settings: ["envoy", "settings"] as const,
        area: ["envoy", "area"] as const,
        saved: ["envoy", "saved"] as const,
        applicationsFor: (postingIds: readonly string[]) =>
            ["envoy", "applications", "postings", ...postingIds] as const,
        applicationOf: (postingId: string) =>
            ["envoy", "applications", "posting", postingId] as const,
        application: (applicationId: string) =>
            ["envoy", "applications", "one", applicationId] as const,
        history: (query: unknown) => ["envoy", "applications", "history", query] as const,
        /** A Jobs tab's page of the person's applications, or its count alone. */
        tracked: (query: unknown) => ["envoy", "applications", "tracked", query] as const,
        fit: (applicationId: string) => ["envoy", "fit", applicationId] as const,
        events: (applicationId: string) => ["envoy", "events", applicationId] as const,
    },
    catalogue: {
        status: ["catalogue", "status"] as const,
        search: (query: unknown) => ["catalogue", "search", query] as const,
        counts: (query: unknown) => ["catalogue", "counts", query] as const,
        places: (query: unknown) => ["catalogue", "places", query] as const,
        here: ["catalogue", "here"] as const,
        titles: (q: string) => ["catalogue", "titles", q] as const,
        page: (query: unknown) => ["catalogue", "page", query] as const,
        posting: (postingId: string) => ["catalogue", "posting", postingId] as const,
        postingEvents: (postingId: string) => ["catalogue", "posting-events", postingId] as const,
    },
    resume: {
        all: ["resume"] as const,
        fits: (postingIds: readonly string[]) => ["resume", "fit", ...postingIds] as const,
        bestFit: (query: unknown) => ["resume", "best-fit", query] as const,
        mostKeywords: (query: unknown) => ["resume", "most-keywords", query] as const,
        tailored: (resumeId: string | null) => ["resume", "tailored", resumeId] as const,
        changes: (resumeId: string) => ["resume", "changes", resumeId] as const,
        version: (version: number) => ["resume", "version", version] as const,
        baseline: ["resume", "baseline"] as const,
        read: (uploadId: string) => ["resume", "read", uploadId] as const,
        demographics: ["resume", "demographics"] as const,
        exports: ["resume", "exports"] as const,
    },
    gateway: {
        models: ["gateway", "models"] as const,
    },
    billing: {
        all: ["billing"] as const,
        usage: (userId: string) => ["billing", "usage", userId] as const,
        claims: (userId: string) => ["billing", "claims", userId] as const,
        plans: ["billing", "plans"] as const,
    },
    notify: {
        subscriptions: ["notify", "subscriptions"] as const,
        preferences: ["notify", "preferences"] as const,
        samples: ["notify", "samples"] as const,
    },
    auth: {
        profile: (userId: string) => ["auth", "profile", userId] as const,
        consents: (userId: string) => ["auth", "consents", userId] as const,
        me: (userId: string) => ["auth", "me", userId] as const,
    },
} as const;

/** The browser's own time zone, which the overview's days are counted in. */
export function browserZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}
